import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';
// Assuming useUIStore exists or will be created
import { useUIStore } from '../../stores/ui';

const ToastItem = ({ toast, onRemove }) => {
  const [progress, setProgress] = useState(100);
  
  useEffect(() => {
    const duration = toast.duration || 5000;
    const interval = 10;
    const step = (interval / duration) * 100;
    
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          onRemove(toast.id);
          return 0;
        }
        return prev - step;
      });
    }, interval);
    
    return () => clearInterval(timer);
  }, [toast, onRemove]);

  const icons = {
    success: <CheckCircle2 className="text-success-500" size={20} />,
    error: <XCircle className="text-danger-500" size={20} />,
    info: <Info className="text-primary-500" size={20} />,
    warning: <AlertTriangle className="text-warning-500" size={20} />,
  };

  const colors = {
    success: 'bg-success-500',
    error: 'bg-danger-500',
    info: 'bg-primary-500',
    warning: 'bg-warning-500',
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 50, scale: 0.9 }}
      layout
      className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-lg rounded-lg overflow-hidden pointer-events-auto relative mb-3 w-80 max-w-[calc(100vw-2rem)]"
    >
      <div className="p-4 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {icons[toast.type || 'info']}
        </div>
        <div className="flex-1 text-sm text-gray-700 dark:text-gray-200 pr-4">
          {toast.message}
        </div>
        <button
          onClick={() => onRemove(toast.id)}
          className="flex-shrink-0 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
      <div className="h-1 bg-gray-100 dark:bg-gray-700 w-full">
        <div 
          className={`h-full ${colors[toast.type || 'info']}`} 
          style={{ width: `${progress}%` }} 
        />
      </div>
    </motion.div>
  );
};

const ToastContainer = () => {
  // Use state from store, fallback to empty array if store not implemented yet
  const toasts = useUIStore((state) => state.toasts) || [];
  const removeToast = useUIStore((state) => state.removeToast) || (() => {});

  return (
    <div className="fixed bottom-0 right-0 z-50 p-4 md:p-6 pointer-events-none flex flex-col items-end">
      <AnimatePresence>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default ToastContainer;
